/**
 * Supported UI locales. Phase 1: ru, en (full). Phase 2: structure + en fallback.
 */

/** UI labels (native language) + short codes for compact picker */
export const SUPPORTED_LANGUAGES = [
    { code: 'en', label: 'English', nativeLabel: 'English', shortCode: 'EN', flag: '🇬🇧', rtl: false },
    { code: 'hy', label: 'Հայերեն', nativeLabel: 'Հայերեն', shortCode: 'HY', flag: '🇦🇲', rtl: false },
    { code: 'ka', label: 'ქართული', nativeLabel: 'ქართული', shortCode: 'KA', flag: '🇬🇪', rtl: false },
    { code: 'tr', label: 'Türkçe', nativeLabel: 'Türkçe', shortCode: 'TR', flag: '🇹🇷', rtl: false },
    { code: 'de', label: 'Deutsch', nativeLabel: 'Deutsch', shortCode: 'DE', flag: '🇩🇪', rtl: false },
    { code: 'fr', label: 'Français', nativeLabel: 'Français', shortCode: 'FR', flag: '🇫🇷', rtl: false },
    { code: 'es', label: 'Español', nativeLabel: 'Español', shortCode: 'ES', flag: '🇪🇸', rtl: false },
    { code: 'pl', label: 'Polski', nativeLabel: 'Polski', shortCode: 'PL', flag: '🇵🇱', rtl: false },
    { code: 'kk', label: 'Қазақша', nativeLabel: 'Қазақша', shortCode: 'KK', flag: '🇰🇿', rtl: false },
    { code: 'uz', label: "O'zbek", nativeLabel: "O'zbek", shortCode: 'UZ', flag: '🇺🇿', rtl: false },
    { code: 'az', label: 'Azərbaycan', nativeLabel: 'Azərbaycan', shortCode: 'AZ', flag: '🇦🇿', rtl: false },
    { code: 'cs', label: 'Čeština', nativeLabel: 'Čeština', shortCode: 'CS', flag: '🇨🇿', rtl: false },
    { code: 'pt-BR', label: 'Português', nativeLabel: 'Português', shortCode: 'PT', flag: '🇧🇷', rtl: false },
    { code: 'ru', label: 'Русский', nativeLabel: 'Русский', shortCode: 'RU', flag: '🇷🇺', rtl: false },
    { code: 'ar', label: 'العربية', nativeLabel: 'العربية', shortCode: 'AR', flag: '🇦🇪', rtl: true },
];

/** Twemoji SVG — avoids Windows rendering flags as duplicate "RU"/"KZ" letters */
export function flagImageUrl(flagEmoji) {
    if (!flagEmoji) return null;
    const cps = [];
    for (const char of flagEmoji) {
        const cp = char.codePointAt(0);
        if (cp && cp !== 0xfe0f) cps.push(cp.toString(16));
    }
    if (!cps.length) return null;
    return `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${cps.join('-')}.svg`;
}

const _hostname = typeof window !== 'undefined'
  ? window.location.hostname : '';
const _isRuDomain = _hostname.includes('sportprogress.ru')
  || _hostname.includes('localhost');

export const DEFAULT_LANGUAGE = _isRuDomain ? 'ru' : 'en';
export const LOCALE_STORAGE_KEY = 'sp_locale';
export const FALLBACK_LANGUAGES = _isRuDomain
  ? ['ru', 'en']
  : ['en', 'ru'];

export const RTL_LANGUAGES = new Set(['ar']);

export function isSupportedLocale(code) {
    if (!code) return false;
    const base = code.split('-')[0];
    return SUPPORTED_LANGUAGES.some((l) => l.code === code || l.code === base);
}

export function normalizeLocale(code) {
    if (!code) return DEFAULT_LANGUAGE;
    const exact = SUPPORTED_LANGUAGES.find((l) => l.code === code);
    if (exact) return exact.code;
    const base = code.split('-')[0];
    const partial = SUPPORTED_LANGUAGES.find((l) => l.code === base);
    return partial?.code ?? DEFAULT_LANGUAGE;
}

export function isRtlLocale(code) {
    const norm = normalizeLocale(code);
    return RTL_LANGUAGES.has(norm) || SUPPORTED_LANGUAGES.some((l) => l.code === norm && l.rtl);
}

export function resolveBrowserLocale() {
    if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;
    const tag = (navigator.language || 'en').toLowerCase();
    if (tag.startsWith('pt-br')) return 'pt-BR';
    return normalizeLocale(tag.split(/[-_]/)[0]);
}

/** Языки, которые нельзя отключить в админке: en — язык по умолчанию на международных доменах, ru — эталон переводов. */
const ALWAYS_ENABLED = ['en', 'ru'];

/**
 * Языки, включённые администратором платформы для этого региона. null — ограничений нет
 * (пока конфигурация не загружена, показываем всё, как раньше).
 */
let enabledCodes = null;

export function setEnabledLanguageCodes(codes) {
    if (!Array.isArray(codes) || codes.length === 0) {
        enabledCodes = null;
        return;
    }
    enabledCodes = new Set([...ALWAYS_ENABLED, ...codes]);
}

export function isLanguageEnabled(code) {
    if (!enabledCodes) return true;
    const normalized = normalizeLocale(code);
    return enabledCodes.has(normalized);
}

/** Языки, доступные пользователю для выбора. Полный список для служебных экранов — SUPPORTED_LANGUAGES. */
export function getAvailableLanguages() {
    return enabledCodes
        ? SUPPORTED_LANGUAGES.filter((l) => enabledCodes.has(l.code))
        : SUPPORTED_LANGUAGES;
}

export function getAvailableLanguageCodes() {
    return getAvailableLanguages().map((l) => l.code);
}

/** Язык, на который переключаем, если выбранный отключили: язык домена, иначе английский. */
export function getFallbackAvailableLanguage() {
    return isLanguageEnabled(DEFAULT_LANGUAGE) ? DEFAULT_LANGUAGE : 'en';
}

/** Device/browser language first, then rest in SUPPORTED_LANGUAGES order. */
export function sortLanguagesForPicker(deviceLangCode) {
    const list = getAvailableLanguages();
    if (!list.length) return [];

    const device = normalizeLocale(deviceLangCode || resolveBrowserLocale());
    const first = list.find((l) => l.code === device)
        || list.find((l) => device.startsWith(l.code.split('-')[0]));
    if (!first) return list;

    return [first, ...list.filter((l) => l.code !== first.code)];
}
